import React, { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { getUserIdFromToken } from "utils/tokenUtils";
import {
  getLeadQuotations,
  upsertLeadQuotation,
  deleteLeadQuotation,
  getJobQuotations,
  upsertJobQuotation,
  deleteJobQuotation,
  getQuotationCategories,
  upsertQuotationCategory,
  deleteQuotationCategory,
  getQuotationWishItems,
  upsertQuotationWishItem,
  deleteQuotationWishItem,
  getQuotationWorkStatuses,
  getQuotationCostStatuses,
  upsertQuotationWorkStatus,
  deleteQuotationWorkStatus,
  upsertQuotationCostStatus,
  deleteQuotationCostStatus,
} from "services/quotationService";
import { getInternalUsersForAssigningLeads } from "services/userService";
import { useDropdown, useDynamicColumns, useLocalStorage } from "hooks";
import { invalidateTimelineCache } from "hooks/useTimeline";
import { DropdownEnum } from "enum/DropdownEnum";
import { EntityTypeEnum } from "enum/EntityTypeEnum";
import { canEditLead } from "permissions/permissionHelpers";
import { canEditJob } from "permissions/permissionHelpers/jobPermissions";

import ClientNotesList from "./ClientNotesList";
import AddEditNoteModal from "./AddEditNoteModal";
import ViewNoteModal from "./ViewNoteModal";
import QuotationStatusChangeModal from "./QuotationStatusChangeModal";
import ViewDocumentModal from "components/leads/LeadDetails/DocumentsTab/components/ViewDocumentModal";

// Module-level cache to persist master data across tab mounts/unmounts
let masterDataCache = null;
let masterDataPromise = null;

export default function ClientNotesTab({   
  type, // "lead" | "job"
  leadId,
  jobId,
  job,
  isMobile = false,
  extraFieldsRequired,
  className,
  onRefreshParent,
  readOnly,
}) {
  const resolvedJobId = jobId || job?.apiData?.Job?.JobId || job?.id;
  const isLead = type === "lead";
  const dynamicFieldsRef = useRef(null);
  // Lists
  const [quotations, setQuotations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [wishItems, setWishItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [workStatuses, setWorkStatuses] = useState([]);
  const [costStatuses, setCostStatuses] = useState([]);

  // Seeding states (for Job)
  const seedQuotations = useMemo(() => job?.apiData?.Quotations || [], [job]);
  const [hasFetchedFromApi, setHasFetchedFromApi] = useState(false);

  // Loaders & Errors
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Pagination & Search States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage("client-notes-page-size", 100);
  const [searchVal, setSearchVal] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedNoteTypes, setSelectedNoteTypes] = useState([]);

  // Modals state
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalMode, setStatusModalMode] = useState("status"); // 'status' | 'costStatus'
  const [previewDocument, setPreviewDocument] = useState(null);

  // Selected records
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingName, setDeletingName] = useState("");
  const [confirmDeleteType, setConfirmDeleteType] = useState(null); // 'note' | 'category' | 'wish'
  const [errors, setErrors] = useState({
    categoryId: "",
    wish: "",
    wishItemId: "",
    noteTypeId: "",
    cost: "",
    assignedToUserId: "",
    workStatusId: "",
    costStatusId: "",
    costAssignedToUserId: "",
  });

  // Form fields
  const [formValues, setFormValues] = useState({
    id: null,
    categoryId: "",
    wish: "",
    wishItemId: "",
    noteTypeId: "",
    cost: "",
    assignedToUserId: "",
    workStatusId: "",
    costStatusId: "",
    costAssignedToUserId: "",
  });

  // Creation states
  const [addingCategory, setAddingCategory] = useState(false);
  const [addingWishItem, setAddingWishItem] = useState(false);
  const [addingWorkStatus, setAddingWorkStatus] = useState(false);
  const [addingCostStatus, setAddingCostStatus] = useState(false);

  const [columnsRefreshKey, setColumnsRefreshKey] = useState(0);
  const noteTypeDropdown = useDropdown(DropdownEnum.ClientNoteType);

  const isExtraFieldsRequired = useMemo(() => {
    if (extraFieldsRequired !== undefined) {
      return extraFieldsRequired;
    }
    return true;
  }, [extraFieldsRequired]);

  // CSS wrapper classes
  const resolvedClassName = useMemo(() => {
    if (className) return className;
    return isLead
      ? "flex-1 flex flex-col h-[calc(100vh-140px)] min-h-0 py-2 px-2 space-y-4"
      : "flex-1 flex flex-col h-[calc(100vh-140px)] min-h-0 py-6 px-6 space-y-4";
  }, [className, isLead]);

  // Seed from job detail response on first render (Job mode only)
  useEffect(() => {
    if (!isLead && seedQuotations.length > 0 && !hasFetchedFromApi) {
      setQuotations(seedQuotations);
      setTotalRecords(seedQuotations.length);
    }
  }, [seedQuotations, isLead, hasFetchedFromApi]);
  // Load dynamic columns for Client Notes (EntityTypeCode = 7 for lead, 8 for job)
  const {
    availableColumns,
    selectedColumnsForApi,
    visibility,
    setVisibility,
    isLoading: isColumnsLoading,
  } = useDynamicColumns(isLead ? EntityTypeEnum.LeadClientNote : EntityTypeEnum.JobClientNote, "client-notes-table-visibility", columnsRefreshKey);

  // Fetch initial master data
  const fetchMasterData = async () => {
    if (masterDataCache) {
      setCategories(masterDataCache.categories);
      setWishItems(masterDataCache.wishItems);
      setUsers(masterDataCache.users);
      setWorkStatuses(masterDataCache.workStatuses);
      setCostStatuses(masterDataCache.costStatuses);
      return;
    }

    if (masterDataPromise) {
      try {
        const cache = await masterDataPromise;
        setCategories(cache.categories);
        setWishItems(cache.wishItems);
        setUsers(cache.users);
        setWorkStatuses(cache.workStatuses);
        setCostStatuses(cache.costStatuses);
      } catch (e) {
        console.error("Failed to load master data from pending promise:", e);
      }
      return;
    }

    masterDataPromise = (async () => {
      const [catsRes, wishRes, usersRes, workRes, costRes] = await Promise.all([
        getQuotationCategories(),
        getQuotationWishItems(),
        getInternalUsersForAssigningLeads(),
        getQuotationWorkStatuses(),
        getQuotationCostStatuses(),
      ]);

      const cache = {
        categories: catsRes.success ? (catsRes.data || []) : [],
        wishItems: wishRes.success ? (wishRes.data || []) : [],
        users: usersRes.success ? (usersRes.data || []) : [],
        workStatuses: workRes.success ? (workRes.data || []) : [],
        costStatuses: costRes.success ? (costRes.data || []) : [],
      };
      masterDataCache = cache;
      return cache;
    })();

    try {
      const cache = await masterDataPromise;
      setCategories(cache.categories);
      setWishItems(cache.wishItems);
      setUsers(cache.users);
      setWorkStatuses(cache.workStatuses);
      setCostStatuses(cache.costStatuses);
    } catch (e) {
      console.error("Failed to load master data:", e);
      masterDataPromise = null;
    }
  };

  // Fetch quotations list
  const fetchQuotations = async () => {
    const targetId = isLead ? leadId : resolvedJobId;
    if (!targetId) return;

    setLoading(true);
    setError(null);
    try {
      let quotesRes;
      if (isLead) {
        quotesRes = await getLeadQuotations({
          leadId: targetId,
          search: debouncedSearch.trim() || null,
          pageNumber: currentPage,
          pageSize: pageSize,
          noteTypes: selectedNoteTypes && selectedNoteTypes.length > 0 ? selectedNoteTypes : null,
          selectedColumns: selectedColumnsForApi,
        });
      } else {
        quotesRes = await getJobQuotations({
          jobId: targetId,
          search: debouncedSearch.trim() || null,
          pageNumber: currentPage,
          pageSize: pageSize,
          noteTypes: selectedNoteTypes && selectedNoteTypes.length > 0 ? selectedNoteTypes : null,
          selectedColumns: selectedColumnsForApi,
        });
      }

      if (quotesRes.success) {
        const rawQuotations = quotesRes.data || [];
        const quotesWithFields = rawQuotations.map((quote) => {
          let dynamicFields = quote.DynamicFields || quote.dynamicFields || [];
          if (typeof dynamicFields === "string") {
            try {
              dynamicFields = JSON.parse(dynamicFields);
            } catch (e) {
              dynamicFields = [];
            }
          }
          return {
            ...quote,
            DynamicFields: dynamicFields,
          };
        });
        setQuotations(quotesWithFields);
        setTotalRecords(quotesRes.totalRecords || 0);
        if (!isLead) {
          setHasFetchedFromApi(true);
        }
      } else {
        setError(quotesRes.error || "Failed to load client notes.");
      }
    } catch (e) {
      console.error(e);
      setError("Failed to load client notes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);



  // Debounce searchVal into debouncedSearch (only trigger search when 3 or more characters are entered)
  useEffect(() => {
    const trimmed = searchVal.trim();
    if (trimmed.length === 0) {
      setDebouncedSearch("");
      setCurrentPage(1);
      return;
    }

    if (trimmed.length < 3) {
      setDebouncedSearch("");
      setCurrentPage(1);
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(trimmed);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchVal]);

  useEffect(() => {
    const targetId = isLead ? leadId : resolvedJobId;
    if (targetId && !isColumnsLoading) {
      fetchQuotations();
    }
  }, [leadId, resolvedJobId, currentPage, pageSize, debouncedSearch, selectedNoteTypes, selectedColumnsForApi, isColumnsLoading, isLead, columnsRefreshKey]);

  // Handle open add / edit modal
  const handleOpenAdd = () => {
    setFormValues({
      id: null,
      categoryId: "",
      wish: "",
      wishItemId: "",
      noteTypeId: "",
      cost: "",
      assignedToUserId: "",
      workStatusId: "",
      costStatusId: "",
      costAssignedToUserId: "",
    });
    setErrors({
      categoryId: "",
      wish: "",
      wishItemId: "",
      noteTypeId: "",
      cost: "",
      assignedToUserId: "",
      workStatusId: "",
      costStatusId: "",
      costAssignedToUserId: "",
    });
    setSelectedQuotation(null);
    setShowAddEditModal(true);
  };

  const handleOpenEdit = (quote) => {
    setFormValues({
      id: quote.Id,
      categoryId: quote.CategoryId || "",
      wish: quote.ClientWish || quote.clientWish || "",
      wishItemId: quote.WishItemId || "",
      noteTypeId: quote.NoteTypeId?.toString()?.toLowerCase() || "",
      cost: quote.Cost || "",
      assignedToUserId: quote.AssignedToUserId || "",
      workStatusId: quote.WorkStatusId || "",
      costStatusId: quote.CostStatusId || "",
      costAssignedToUserId: quote.CostAssignedToUserId || "",
    });
    setErrors({
      categoryId: "",
      wish: "",
      wishItemId: "",
      noteTypeId: "",
      cost: "",
      assignedToUserId: "",
      workStatusId: "",
      costStatusId: "",
      costAssignedToUserId: "",
    });
    setSelectedQuotation(quote);
    if (showViewModal) {
      setTimeout(() => setShowAddEditModal(true), 250);
    } else {
      setShowAddEditModal(true);
    }
  };

  const handleOpenView = (quote) => {
    setSelectedQuotation(quote);
    setShowViewModal(true);
  };

  const handleOpenDelete = (id) => {
    setDeletingId(id);
    setConfirmDeleteType("note");
    setShowDeleteConfirm(true);
  };

  const handleOpenStatusModal = (quote, mode) => {
    setSelectedQuotation(quote);
    setStatusModalMode(mode);
    setShowStatusModal(true);
  };

  // Status Badge Colors mapping
  const getWorkStatusColor = (statusName) => {
    const name = statusName?.toLowerCase() || "";
    if (name === "completed") {
      return "bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/20";
    }
    if (name === "delivered") {
      return "bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20";
    }
    if (name === "ordered") {
      return "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20";
    }
    return "bg-gray-500/10 text-gray-700 dark:text-gray-400 border border-gray-500/20";
  };

  const getCostStatusColor = (statusName) => {
    const name = statusName?.toLowerCase() || "";
    if (name === "invoiced to client") {
      return "bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/20";
    }
    if (name === "standard") {
      return "bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20";
    }
    return "bg-gray-500/10 text-gray-700 dark:text-gray-400 border border-gray-500/20";
  };

  // Create category option from searchable select dropdown
  const handleCreateCategory = async (inputValue) => {
    if (!inputValue || !inputValue.trim()) return;
    setAddingCategory(true);
    try {
      const res = await upsertQuotationCategory(inputValue.trim());
      if (res.success && res.data) {
        toast.success("Category created successfully");
        const newCat = res.data;
        setCategories((prev) => [...prev, newCat]);
        setFormValues((prev) => ({
          ...prev,
          categoryId: newCat.Id,
        }));
      } else {
        toast.error(res.error || "Failed to create category");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error creating category");
    } finally {
      setAddingCategory(false);
    }
  };

  // Edit category from searchable select list
  const handleEditCategory = async (id, currentName, inlineName) => {
    const newName = inlineName !== undefined ? inlineName : window.prompt("Edit Category Name:", currentName);
    if (!newName || !newName.trim() || newName.trim() === currentName) return;

    setAddingCategory(true);
    try {
      const res = await upsertQuotationCategory(newName.trim(), parseInt(id));
      if (res.success && res.data) {
        toast.success("Category updated successfully");
        const updatedCat = res.data;
        setCategories((prev) =>
          prev.map((c) => (c.Id === updatedCat.Id ? updatedCat : c))
        );
      } else {
        toast.error(res.error || "Failed to update category");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error updating category");
    } finally {
      setAddingCategory(false);
    }
  };

  // Delete category from list
  const handleDeleteCategory = (id, name) => {
    setDeletingId(id);
    setDeletingName(name);
    setConfirmDeleteType("category");
    setShowDeleteConfirm(true);
  };

  // Create wish item from searchable select dropdown
  const handleCreateWishItem = async (inputValue) => {
    if (!inputValue || !inputValue.trim()) return;
    setAddingWishItem(true);
    try {
      const res = await upsertQuotationWishItem(inputValue.trim());
      if (res.success && res.data) {
        toast.success("Wish item created successfully");
        const newWish = res.data;
        setWishItems((prev) => [...prev, newWish]);
        setFormValues((prev) => ({
          ...prev,
          wishItemId: newWish.Id,
        }));
      } else {
        toast.error(res.error || "Failed to create wish item");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error creating wish item");
    } finally {
      setAddingWishItem(false);
    }
  };

  // Edit wish item from list
  const handleEditWishItem = async (id, currentName, inlineName) => {
    const newName = inlineName !== undefined ? inlineName : window.prompt("Edit Wish Item Name:", currentName);
    if (!newName || !newName.trim() || newName.trim() === currentName) return;

    setAddingWishItem(true);
    try {
      const res = await upsertQuotationWishItem(newName.trim(), parseInt(id));
      if (res.success && res.data) {
        toast.success("Wish item updated successfully");
        const updatedWish = res.data;
        setWishItems((prev) =>
          prev.map((w) => (w.Id === updatedWish.Id ? updatedWish : w))
        );
      } else {
        toast.error(res.error || "Failed to update wish item");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error updating wish item");
    } finally {
      setAddingWishItem(false);
    }
  };

  // Delete wish item from list
  const handleDeleteWishItem = (id, name) => {
    setDeletingId(id);
    setDeletingName(name);
    setConfirmDeleteType("wish");
    setShowDeleteConfirm(true);
  };

  // Create work status from searchable select dropdown
  const handleCreateWorkStatus = async (inputValue) => {
    if (!inputValue || !inputValue.trim()) return { success: false };
    setAddingWorkStatus(true);
    try {
      const res = await upsertQuotationWorkStatus(inputValue.trim());
      if (res.success && res.data) {
        toast.success("Work status created successfully");
        const newStatus = res.data;
        setWorkStatuses((prev) => [...prev, newStatus]);
        setFormValues((prev) => ({
          ...prev,
          workStatusId: newStatus.Id,
        }));
        return { success: true, optionId: newStatus.Id };
      } else {
        toast.error(res.error || "Failed to create work status");
        return { success: false };
      }
    } catch (e) {
      console.error(e);
      toast.error("Error creating work status");
      return { success: false };
    } finally {
      setAddingWorkStatus(false);
    }
  };

  // Edit work status from list
  const handleEditWorkStatus = async (id, currentName, inlineName) => {
    const newName = inlineName !== undefined ? inlineName : window.prompt("Edit Work Status Name:", currentName);
    if (!newName || !newName.trim() || newName.trim() === currentName) return;

    setAddingWorkStatus(true);
    try {
      const res = await upsertQuotationWorkStatus(newName.trim(), parseInt(id));
      if (res.success && res.data) {
        toast.success("Work status updated successfully");
        const updatedStatus = res.data;
        setWorkStatuses((prev) =>
          prev.map((s) => (s.Id === updatedStatus.Id ? updatedStatus : s))
        );
        fetchQuotations();
      } else {
        toast.error(res.error || "Failed to update work status");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error updating work status");
    } finally {
      setAddingWorkStatus(false);
    }
  };

  // Delete work status from list
  const handleDeleteWorkStatus = (id, name) => {
    setDeletingId(id);
    setDeletingName(name);
    setConfirmDeleteType("workStatus");
    setShowDeleteConfirm(true);
  };

  // Create cost status from searchable select dropdown
  const handleCreateCostStatus = async (inputValue) => {
    if (!inputValue || !inputValue.trim()) return { success: false };
    setAddingCostStatus(true);
    try {
      const res = await upsertQuotationCostStatus(inputValue.trim());
      if (res.success && res.data) {
        toast.success("Cost status created successfully");
        const newStatus = res.data;
        setCostStatuses((prev) => [...prev, newStatus]);
        setFormValues((prev) => ({
          ...prev,
          costStatusId: newStatus.Id,
        }));
        return { success: true, optionId: newStatus.Id };
      } else {
        toast.error(res.error || "Failed to create cost status");
        return { success: false };
      }
    } catch (e) {
      console.error(e);
      toast.error("Error creating cost status");
      return { success: false };
    } finally {
      setAddingCostStatus(false);
    }
  };

  // Edit cost status from list
  const handleEditCostStatus = async (id, currentName, inlineName) => {
    const newName = inlineName !== undefined ? inlineName : window.prompt("Edit Cost Status Name:", currentName);
    if (!newName || !newName.trim() || newName.trim() === currentName) return;

    setAddingCostStatus(true);
    try {
      const res = await upsertQuotationCostStatus(newName.trim(), parseInt(id));
      if (res.success && res.data) {
        toast.success("Cost status updated successfully");
        const updatedStatus = res.data;
        setCostStatuses((prev) =>
          prev.map((s) => (s.Id === updatedStatus.Id ? updatedStatus : s))
        );
        fetchQuotations();
      } else {
        toast.error(res.error || "Failed to update cost status");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error updating cost status");
    } finally {
      setAddingCostStatus(false);
    }
  };

  // Delete cost status from list
  const handleDeleteCostStatus = (id, name) => {
    setDeletingId(id);
    setDeletingName(name);
    setConfirmDeleteType("costStatus");
    setShowDeleteConfirm(true);
  };

  // Helper to strip HTML tags for validation
  const stripHtml = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  };

  // Helper to check if rich text HTML content is empty (accounting for images/embeds)
  const isRichTextEmpty = (html) => {
    if (!html) return true;
    if (/<(img|svg|iframe|video|audio|object|embed|canvas)[^>]*>/i.test(html)) {
      return false;
    }
    return !stripHtml(html);
  };

  // Handle Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {
      categoryId: "",
      wish: "",
      wishItemId: "",
      noteTypeId: "",
      cost: "",
      assignedToUserId: "",
      workStatusId: "",
      costStatusId: "",
      costAssignedToUserId: "",
    };
    let hasError = false;

    if (!formValues.noteTypeId) {
      newErrors.noteTypeId = "Note type is required";
      hasError = true;
    }
    if (!formValues.categoryId) {
      newErrors.categoryId = "Category is required";
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) {
      const firstError = Object.values(newErrors).find(err => err !== "");
      if (firstError) {
        toast.error(firstError);
      } else {
        toast.error("Please fill all required fields");
      }
      return;
    }

    setSubmitting(true);
    try {
      const basePayload = {
        id: formValues.id,
        categoryId: parseInt(formValues.categoryId),
        cost: formValues.cost ? formValues.cost.trim() : "",
        assignedToUserId: formValues.assignedToUserId || null,
        workStatusId: formValues.workStatusId ? parseInt(formValues.workStatusId) : null,
        costStatusId: formValues.costStatusId ? parseInt(formValues.costStatusId) : null,
        costAssignedToUserId: formValues.costAssignedToUserId || null,
        userId: getUserIdFromToken(),
        noteTypeId: formValues.noteTypeId || null,
        clientWish: formValues.wish,
      };

      let res;
      if (isLead) {
        res = await upsertLeadQuotation({ ...basePayload, leadId });
      } else {
        res = await upsertJobQuotation({ ...basePayload, jobId: resolvedJobId });
      }

      if (res.success) {
        const targetId = formValues.id || res.data?.Id || res.data?.id;
        if (targetId && dynamicFieldsRef.current) {
          try {
            await dynamicFieldsRef.current.save(targetId);
          } catch (saveError) {
            console.error("Error saving client note dynamic fields:", saveError);
            toast.error("Failed to save dynamic fields");
          }
        }
        toast.success(formValues.id ? "Client Note updated successfully" : "Client Note added successfully");
        setShowAddEditModal(false);
        if (!isLead) {
          setHasFetchedFromApi(false);
        }
        setColumnsRefreshKey((prev) => prev + 1);
        if (onRefreshParent) {
          onRefreshParent(isLead ? leadId : resolvedJobId);
        }
        invalidateTimelineCache(isLead ? leadId : null, isLead ? null : resolvedJobId);
        fetchQuotations();
      } else {
        toast.error(res.error || "Failed to save client note");
      }
    } catch (err) {
      console.error(err);
      toast.error("An unexpected error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Delete Confirmation (General for note, category, wish item)
  const handleDeleteConfirm = async () => {
    if (!deletingId) return;
    setSubmitting(true);
    try {
      if (confirmDeleteType === "note") {
        const res = isLead
          ? await deleteLeadQuotation(deletingId)
          : await deleteJobQuotation(deletingId);

        if (res.success) {
          toast.success("Client Note deleted successfully");
          setShowDeleteConfirm(false);
          setDeletingId(null);
          setConfirmDeleteType(null);
          invalidateTimelineCache(isLead ? leadId : null, isLead ? null : resolvedJobId);
          fetchQuotations();
        } else {
          toast.error(res.error || "Failed to delete client note");
        }
      } else if (confirmDeleteType === "category") {
        const res = await deleteQuotationCategory(parseInt(deletingId));
        if (res.success) {
          toast.success("Category deleted successfully");
          setCategories((prev) => prev.filter((c) => c.Id !== parseInt(deletingId)));
          setFormValues((prev) => {
            if (prev.categoryId?.toString() === deletingId.toString()) {
              return { ...prev, categoryId: "" };
            }
            return prev;
          });
          setShowDeleteConfirm(false);
          setDeletingId(null);
          setConfirmDeleteType(null);
        } else {
          toast.error(res.error || "Failed to delete category");
        }
      } else if (confirmDeleteType === "wish") {
        const res = await deleteQuotationWishItem(parseInt(deletingId));
        if (res.success) {
          toast.success("Wish item deleted successfully");
          setWishItems((prev) => prev.filter((w) => w.Id !== parseInt(deletingId)));
          setFormValues((prev) => {
            if (prev.wishItemId?.toString() === deletingId.toString()) {
              return { ...prev, wishItemId: "" };
            }
            return prev;
          });
          setShowDeleteConfirm(false);
          setDeletingId(null);
          setConfirmDeleteType(null);
        } else {
          toast.error(res.error || "Failed to delete wish item");
        }
      } else if (confirmDeleteType === "workStatus") {
        const res = await deleteQuotationWorkStatus(parseInt(deletingId));
        if (res.success) {
          toast.success("Work status deleted successfully");
          setWorkStatuses((prev) => prev.filter((s) => s.Id !== parseInt(deletingId)));
          setFormValues((prev) => {
            if (prev.workStatusId?.toString() === deletingId.toString()) {
              return { ...prev, workStatusId: "" };
            }
            return prev;
          });
          setShowDeleteConfirm(false);
          setDeletingId(null);
          setConfirmDeleteType(null);
          fetchQuotations();
        } else {
          toast.error(res.error || "Failed to delete work status");
        }
      } else if (confirmDeleteType === "costStatus") {
        const res = await deleteQuotationCostStatus(parseInt(deletingId));
        if (res.success) {
          toast.success("Cost status deleted successfully");
          setCostStatuses((prev) => prev.filter((s) => s.Id !== parseInt(deletingId)));
          setFormValues((prev) => {
            if (prev.costStatusId?.toString() === deletingId.toString()) {
              return { ...prev, costStatusId: "" };
            }
            return prev;
          });
          setShowDeleteConfirm(false);
          setDeletingId(null);
          setConfirmDeleteType(null);
          fetchQuotations();
        } else {
          toast.error(res.error || "Failed to delete cost status");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("An unexpected error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const categoryOptions = useMemo(() => {
    return categories.map((c) => ({
      value: c.Id.toString(),
      label: c.Name,
    }));
  }, [categories]);

  const wishItemOptions = useMemo(() => {
    return wishItems.map((w) => ({
      value: w.Id.toString(),
      label: w.WishItemName,
    }));
  }, [wishItems]);

  const workStatusOptions = useMemo(() => {
    return workStatuses.map((w) => ({
      value: w.Id.toString(),
      label: w.StatusName,
      companyId: w.CompanyId,
    }));
  }, [workStatuses]);

  const costStatusOptions = useMemo(() => {
    return costStatuses.map((c) => ({
      value: c.Id.toString(),
      label: c.StatusName,
      companyId: c.CompanyId,
    }));
  }, [costStatuses]);

  const isStatusOptionActionable = (option) => {
    return option && option.companyId !== null && option.companyId !== undefined;
  };

  const confirmMessages = useMemo(() => {
    if (confirmDeleteType === "category") {
      return {
        pending: {
          title: "Delete Category?",
          description: `Are you sure you want to delete the category "${deletingName}"? Once deleted, it cannot be restored.`,
          actionText: "Delete",
        },
        success: {
          title: "Category Deleted",
          description: "Successfully deleted the category from database.",
        },
      };
    }
    if (confirmDeleteType === "wish") {
      return {
        pending: {
          title: "Delete Wish Item?",
          description: `Are you sure you want to delete the wish item "${deletingName}"? Once deleted, it cannot be restored.`,
          actionText: "Delete",
        },
        success: {
          title: "Wish Item Deleted",
          description: "Successfully deleted the wish item from database.",
        },
      };
    }
    if (confirmDeleteType === "workStatus") {
      return {
        pending: {
          title: "Delete Work Status?",
          description: `Are you sure you want to delete the work status "${deletingName}"? Once deleted, it cannot be restored.`,
          actionText: "Delete",
        },
        success: {
          title: "Work Status Deleted",
          description: "Successfully deleted the work status from database.",
        },
      };
    }
    if (confirmDeleteType === "costStatus") {
      return {
        pending: {
          title: "Delete Cost Status?",
          description: `Are you sure you want to delete the cost status "${deletingName}"? Once deleted, it cannot be restored.`,
          actionText: "Delete",
        },
        success: {
          title: "Cost Status Deleted",
          description: "Successfully deleted the cost status from database.",
        },
      };
    }
    return {
      pending: {
        title: "Delete Client Note?",
        description: "Are you sure you want to delete this record? Once deleted, it cannot be restored.",
        actionText: "Delete",
      },
      success: {
        title: "Record Deleted",
        description: "Successfully deleted the client note from database.",
      },
    };
  }, [confirmDeleteType, deletingName]);

  if (error && quotations.length === 0) {
    return (
      <div className="p-5 text-center text-red-500 dark:text-red-400 font-semibold">
        {error}
      </div>
    );
  }

  return (
    <div className={resolvedClassName}>
      {/* Table list view */}
      <ClientNotesList
        data={quotations}
        totalRecords={totalRecords}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        search={searchVal}
        setSearch={setSearchVal}
        selectedNoteTypes={selectedNoteTypes}
        setSelectedNoteTypes={setSelectedNoteTypes}
        noteTypeOptions={noteTypeDropdown.options}
        noteTypeLoading={noteTypeDropdown.isLoading}
        loading={loading || isColumnsLoading}
        onView={handleOpenView}
        onEdit={handleOpenEdit}
        onDelete={handleOpenDelete}
        onAddClick={handleOpenAdd}
        getWorkStatusColor={getWorkStatusColor}
        getCostStatusColor={getCostStatusColor}
        isMobile={isLead ? isMobile : false}
        onStatusClick={handleOpenStatusModal}
        onPreviewDocument={setPreviewDocument}
        users={users}
        workStatuses={workStatuses}
        costStatuses={costStatuses}
        availableColumns={availableColumns}
        visibility={visibility}
        setVisibility={setVisibility}
        canEdit={isLead ? canEditLead() : (canEditJob() && !readOnly)}
      />

      {/* Add / Edit Modal */}
      <AddEditNoteModal
        isLead={isLead}
        dynamicFieldsRef={dynamicFieldsRef}
        entityTypeCode={isLead ? EntityTypeEnum.LeadClientNote : EntityTypeEnum.JobClientNote}
        show={showAddEditModal}
        onClose={() => setShowAddEditModal(false)}
        formValues={formValues}
        setFormValues={setFormValues}
        submitting={submitting}
        onSubmit={handleSubmit}
        categories={categories}
        addingCategory={addingCategory}
        handleCreateCategory={handleCreateCategory}
        handleEditCategory={handleEditCategory}
        handleDeleteCategory={handleDeleteCategory}
        wishItems={wishItems}
        addingWishItem={addingWishItem}
        handleCreateWishItem={handleCreateWishItem}
        handleEditWishItem={handleEditWishItem}
        handleDeleteWishItem={handleDeleteWishItem}
        wishItemOptions={wishItemOptions}
        noteTypeOptions={noteTypeDropdown.options}
        noteTypeLoading={noteTypeDropdown.isLoading}
        onCreateNoteType={async (name) => {
          const res = await noteTypeDropdown.onCreateOption(name);
          if (res.success && res.optionId) {
            setFormValues(prev => ({ ...prev, noteTypeId: res.optionId }));
          }
        }}
        onEditNoteType={async (id, currentLabel, newText) => {
          const success = await noteTypeDropdown.onEditOption(id, currentLabel, newText);
          if (success && formValues.noteTypeId === id) {
            setFormValues(prev => ({ ...prev, noteTypeId: id }));
          }
        }}
        onDeleteNoteType={async (id, label) => {
          const success = await noteTypeDropdown.onDeleteOption(id, label);
          if (success && formValues.noteTypeId === id) {
            setFormValues(prev => ({ ...prev, noteTypeId: "" }));
          }
        }}
        users={users}
        costAssignUsers={users}
        workStatuses={workStatuses}
        costStatuses={costStatuses}
        categoryOptions={categoryOptions}
        errors={errors}
        setErrors={setErrors}
        extraFieldsRequired={isExtraFieldsRequired}
        workStatusOptions={workStatusOptions}
        addingWorkStatus={addingWorkStatus}
        handleCreateWorkStatus={handleCreateWorkStatus}
        handleEditWorkStatus={handleEditWorkStatus}
        handleDeleteWorkStatus={handleDeleteWorkStatus}
        costStatusOptions={costStatusOptions}
        addingCostStatus={addingCostStatus}
        handleCreateCostStatus={handleCreateCostStatus}
        handleEditCostStatus={handleEditCostStatus}
        handleDeleteCostStatus={handleDeleteCostStatus}
        isStatusOptionActionable={isStatusOptionActionable}
        onFieldAdded={() => setColumnsRefreshKey(prev => prev + 1)}
      />

      {/* View Details Modal */}
      <ViewNoteModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        selectedQuotation={selectedQuotation}
        getWorkStatusColor={getWorkStatusColor}
        getCostStatusColor={getCostStatusColor}
        users={users}
        workStatuses={workStatuses}
        costStatuses={costStatuses}
        onEdit={!readOnly ? handleOpenEdit : undefined}
        onDelete={!readOnly ? handleOpenDelete : undefined}
        availableColumns={availableColumns}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showDeleteConfirm}
        state="pending"
        confirmLoading={submitting}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeletingId(null);
          setConfirmDeleteType(null);
          setDeletingName("");
        }}
        onOk={handleDeleteConfirm}
        messages={confirmMessages}
        buttonColor="error"
      />

      {/* Quotation Status / Cost Status Change Modal */}
      <QuotationStatusChangeModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedQuotation(null);
        }}
        mode={statusModalMode}
        quotation={selectedQuotation}
        onStatusChanged={fetchQuotations}
        workStatusOptions={workStatusOptions}
        costStatusOptions={costStatusOptions}
        addingWorkStatus={addingWorkStatus}
        addingCostStatus={addingCostStatus}
        handleCreateWorkStatus={handleCreateWorkStatus}
        handleEditWorkStatus={handleEditWorkStatus}
        handleDeleteWorkStatus={handleDeleteWorkStatus}
        handleCreateCostStatus={handleCreateCostStatus}
        handleEditCostStatus={handleEditCostStatus}
        handleDeleteCostStatus={handleDeleteCostStatus}
        isStatusOptionActionable={isStatusOptionActionable}
      />

      {/* Document Preview Modal */}
      {previewDocument && (
        <ViewDocumentModal
          isOpen={true}
          onClose={() => setPreviewDocument(null)}
          document={previewDocument}
          entityType={null}
        />
      )}
    </div>
  );
}
